import type { Candle, IndicatorParams, IndicatorSelection } from './types';
import { computeMomentum, defaultMomentumParams, momentumParams, momentumParamsValid } from './momentum';

export interface IndicatorPoint {
  time: string;
  value: number;
}
export interface IndicatorLine {
  label: string;
  color: string;
  width: 1 | 2;
  points: IndicatorPoint[];
}
export interface IndicatorMarker {
  time: string;
  kind: 'enter' | 'exit';
  label: string;
}
export interface IndicatorOutput {
  lines: IndicatorLine[];
  markers: IndicatorMarker[];
  warnings: string[];
}

/** Chart context handed to calculate(); auxSeries holds the extra candle series requested via auxSeries(). */
export interface IndicatorContext {
  interval: string;
  assetClass: string | null;
  timezone: string;
  auxSeries: Partial<Record<string, Candle[]>>;
}

export interface IndicatorParamSpec {
  key: string;
  label: string;
  kind: 'number' | 'toggle' | 'symbol' | 'interval';
  step?: number;
}
/** Extra candle series an indicator needs. symbol/interval null = the charted instrument/interval. */
export interface AuxSeriesRequest {
  key: string;
  symbol: string | null;
  interval: string | null;
  limit: number;
}

export interface IndicatorDefinition {
  id: string;
  version: number;
  label: string;
  description: string;
  placement: 'overlay' | 'pane';
  color: string;
  defaults: IndicatorParams;
  params: IndicatorParamSpec[];
  validate(params: IndicatorParams): boolean;
  auxSeries?(params: IndicatorParams): AuxSeriesRequest[];
  calculate(candles: Candle[], params: IndicatorParams, context: IndicatorContext): IndicatorOutput;
}

export function parseSymbolKey(symbol: string): { venue: string; instrumentId: string } | null {
  const index = symbol.indexOf(':');
  if (index <= 0 || index === symbol.length - 1) return null;
  return { venue: symbol.slice(0, index), instrumentId: symbol.slice(index + 1) };
}

const close = (candle: Candle): number => Number(candle.close);
const periodValid = (params: IndicatorParams) =>
  Number.isInteger(params.period) && Number(params.period) >= 2 && Number(params.period) <= 500;
const singleLine = (label: string, color: string, points: IndicatorPoint[]): IndicatorOutput => ({
  lines: [{ label, color, width: 2, points }],
  markers: [],
  warnings: [],
});

export function sma(candles: Candle[], period: number): IndicatorPoint[] {
  let sum = 0;
  return candles.flatMap((candle, index) => {
    sum += close(candle);
    if (index >= period) sum -= close(candles[index - period]);
    return index + 1 < period ? [] : [{ time: candle.time, value: sum / period }];
  });
}

export function ema(candles: Candle[], period: number): IndicatorPoint[] {
  if (candles.length < period) return [];
  const seed = candles.slice(0, period).reduce((sum, candle) => sum + close(candle), 0) / period;
  const alpha = 2 / (period + 1);
  let value = seed;
  const output: IndicatorPoint[] = [{ time: candles[period - 1].time, value }];
  for (let index = period; index < candles.length; index += 1) {
    value = alpha * close(candles[index]) + (1 - alpha) * value;
    output.push({ time: candles[index].time, value });
  }
  return output;
}

function sessionKey(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

export function vwap(candles: Candle[], timezone: string): IndicatorPoint[] {
  let key = '';
  let priceVolume = 0;
  let volume = 0;
  return candles.flatMap((candle) => {
    const nextKey = sessionKey(candle.time, timezone);
    if (nextKey !== key) {
      key = nextKey;
      priceVolume = 0;
      volume = 0;
    }
    const nextVolume = Number(candle.volume);
    if (!Number.isFinite(nextVolume) || nextVolume <= 0) return [];
    const typical = (Number(candle.high) + Number(candle.low) + Number(candle.close)) / 3;
    priceVolume += typical * nextVolume;
    volume += nextVolume;
    return [{ time: candle.time, value: priceVolume / volume }];
  });
}

const periodSpec: IndicatorParamSpec[] = [{ key: 'period', label: 'Period', kind: 'number' }];

const momentumSpec: IndicatorParamSpec[] = [
  { key: 'win', label: 'Fast window', kind: 'number' },
  { key: 'win2', label: 'Slow window', kind: 'number' },
  { key: 'fastSharpeSMA', label: 'Fast SMA', kind: 'number' },
  { key: 'slowSharpeSMA', label: 'Slow SMA', kind: 'number' },
  { key: 'thresh', label: 'Slow threshold', kind: 'number', step: 0.1 },
  { key: 'shorthresh', label: 'Fast threshold', kind: 'number', step: 0.1 },
  { key: 'volLookback', label: 'Vol lookback', kind: 'number' },
  { key: 'baseAnnVol', label: 'Baseline ann. vol', kind: 'number', step: 0.01 },
  { key: 'minWinCap', label: 'Min window', kind: 'number' },
  { key: 'maxWinCap', label: 'Max window', kind: 'number' },
  { key: 'rfAnnual', label: 'Risk-free (annual)', kind: 'number', step: 0.0001 },
  { key: 'periodsPerYear', label: 'Periods/yr (0 = auto)', kind: 'number' },
  { key: 'useLogReturns', label: 'Log returns', kind: 'toggle' },
  { key: 'useRegime', label: 'Regime gate', kind: 'toggle' },
  { key: 'regimeSymbol', label: 'Regime symbol', kind: 'symbol' },
  { key: 'use200', label: 'Long-SMA filter', kind: 'toggle' },
  { key: 'maSymbol', label: 'Filter symbol (blank = chart)', kind: 'symbol' },
  { key: 'maInterval', label: 'Filter interval', kind: 'interval' },
  { key: 'maLen', label: 'Filter SMA length', kind: 'number' },
  { key: 'maRatio', label: 'Crossunder ratio', kind: 'number', step: 0.001 },
];

export const indicatorRegistry: IndicatorDefinition[] = [
  {
    id: 'sma',
    version: 1,
    label: 'SMA',
    description: 'Simple moving average',
    placement: 'overlay',
    color: '#f4b740',
    defaults: { period: 20 },
    params: periodSpec,
    validate: periodValid,
    calculate: (candles, p) => singleLine('SMA', '#f4b740', sma(candles, Number(p.period))),
  },
  {
    id: 'ema',
    version: 1,
    label: 'EMA',
    description: 'Exponential moving average',
    placement: 'overlay',
    color: '#b388ff',
    defaults: { period: 20 },
    params: periodSpec,
    validate: periodValid,
    calculate: (candles, p) => singleLine('EMA', '#b388ff', ema(candles, Number(p.period))),
  },
  {
    id: 'vwap',
    version: 1,
    label: 'VWAP',
    description: 'Session volume-weighted average price',
    placement: 'overlay',
    color: '#39b9ff',
    defaults: {},
    params: [],
    validate: () => true,
    calculate: (candles, _p, context) => singleLine('VWAP', '#39b9ff', vwap(candles, context.timezone)),
  },
  {
    id: 'sugon-range',
    version: 1,
    label: 'Sugon Range %',
    description: 'Candle range as a percent of close',
    placement: 'pane',
    color: '#36d399',
    defaults: {},
    params: [],
    validate: () => true,
    calculate: (candles) =>
      singleLine(
        'Range %',
        '#36d399',
        candles
          .map((c) => ({ time: c.time, value: ((Number(c.high) - Number(c.low)) / Number(c.close)) * 100 }))
          .filter((p) => Number.isFinite(p.value)),
      ),
  },
  {
    id: 'momentum-sharpe',
    version: 1,
    label: 'Momentum (Rolling Sharpe)',
    description: 'Fast/slow rolling Sharpe with optional regime-symbol gate and long-SMA filter; ▲/▼ mark entry/exit',
    placement: 'pane',
    color: '#f23645',
    defaults: { ...defaultMomentumParams },
    params: momentumSpec,
    validate: momentumParamsValid,
    auxSeries: (raw) => {
      const p = momentumParams(raw);
      const requests: AuxSeriesRequest[] = [];
      if (p.useRegime && p.regimeSymbol && parseSymbolKey(p.regimeSymbol)) {
        requests.push({ key: 'regime', symbol: p.regimeSymbol, interval: null, limit: 1500 });
      }
      if (p.use200) {
        const symbol = p.maSymbol && parseSymbolKey(p.maSymbol) ? p.maSymbol : null;
        requests.push({ key: 'filter', symbol, interval: p.maInterval, limit: Math.min(5000, p.maLen + 300) });
      }
      return requests;
    },
    calculate: (candles, params, context) => {
      const result = computeMomentum(candles, params, {
        interval: context.interval,
        assetClass: context.assetClass,
        regimeCandles: context.auxSeries.regime,
        filterCandles: context.auxSeries.filter,
      });
      return { lines: result.lines, markers: result.markers, warnings: result.warnings };
    },
  },
];

export function resolveIndicators(
  selections: IndicatorSelection[],
): Array<{ definition: IndicatorDefinition; selection: IndicatorSelection }> {
  return selections.flatMap((selection) => {
    const definition = indicatorRegistry.find((item) => item.id === selection.id && item.version === selection.version);
    return definition && selection.enabled && definition.validate(selection.params) ? [{ definition, selection }] : [];
  });
}
